import { WeighingTicketData } from '@/components/WeighingTicket';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Génère un QR code en base64 pour le ticket
 */
const generateQRCode = (data: string): string => {
  // Utilisation de l'API QR Server pour générer le QR code
  // Fallback vers une URL de base64 si l'API n'est pas disponible
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(data)}&format=png&ecc=M`;
  return qrUrl;
};

/**
 * Génère un QR code de fallback en cas de problème de connexion
 */
const generateFallbackQRCode = (data: string): string => {
  // QR code simple en SVG comme fallback
  return `data:image/svg+xml;base64,${btoa(`
    <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="100" fill="white" stroke="black" stroke-width="1"/>
      <text x="50" y="50" text-anchor="middle" font-family="monospace" font-size="8" fill="black">QR</text>
      <text x="50" y="60" text-anchor="middle" font-family="monospace" font-size="6" fill="black">${data.substring(0, 10)}...</text>
    </svg>
  `)}`;
};

/**
 * Fonction pour obtenir le sous-titre selon le type de pesée
 */
const getTicketSubtitle = (typePesee?: string): string => {
  switch (typePesee?.toLowerCase()) {
    case 'reception':
      return 'RECEPTION';
    case 'expedition':
      return 'EXPEDITION';
    case 'transfert':
      return 'TRANSFERT';
    default:
      return 'RECEPTION'; // Par défaut
  }
};

/**
 * Génère le HTML d'impression du ticket
 */
export const generatePrintHTML = (ticketData: WeighingTicketData): string => {
  // Debug: Vérifier les données reçues
  
  const formatWeight = (weight: number) => `${weight.toLocaleString()} kg`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-FR');
  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Helper function to check if a value is valid (not empty, not null, not undefined)
  // Note: 0 is a valid value for numeric fields
  const isValidValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    // For numeric values, 0 is valid
    if (typeof value === 'number') {
      return !isNaN(value);
    }
    // For strings, check if not empty
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return true;
  };

  // Génération du QR code avec les informations principales du ticket
  const qrData = `Ticket: ${ticketData.code}|Poids: ${ticketData.grossWeight}kg|Net: ${ticketData.netWeight}kg|Date: ${ticketData.weighingDate || formatDate(ticketData.grossAt)}`;
  const qrCodeUrl = generateQRCode(qrData);
  const fallbackQRCode = generateFallbackQRCode(qrData);
  
  // Calculer le sous-titre avant de l'insérer dans le template
  const ticketSubtitle = getTicketSubtitle(ticketData.typePesee);

  return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Ticket de Pesée - ${ticketData.code}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Arial', sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #000;
          background: white;
          padding: 8px;
          margin: 0;
        }
        
        .ticket-container {
          max-width: 100%;
          margin: 0 auto;
          border: 1px solid #000;
          padding: 16px;
          background: white;
        }
        
        .header {
          text-align: center;
          border-bottom: 1px solid #000;
          padding-bottom: 8px;
          margin-bottom: 10px;
        }
        
        .logo-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
          gap: 15px;
        }
        
        .logo {
          display: flex;
          align-items: center;
        }
        
        .logo-circle {
          width: 20px;
          height: 20px;
          background: #16a34a;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 6px;
          color: white;
          font-weight: bold;
          font-size: 8px;
        }
        
        .logo-img {
          height: 40px;
          width: auto;
          margin-right: 8px;
          object-fit: contain;
        }
        
        .logo-pontcita {
          height: 35px;
          width: auto;
          object-fit: contain;
        }
        
        .qr-code {
          width: 80px;
          height: 80px;
          border: 1px solid #ddd;
          border-radius: 4px;
          margin-left: 10px;
        }
        
        .organization {
          text-align: left;
        }
        
        .organization h3 {
          font-size: 12px;
          font-weight: bold;
          margin-bottom: 2px;
        }
        
        .organization p {
          font-size: 9px;
          color: #666;
        }
        
        .date-info {
          text-align: right;
          font-size: 10px;
        }
        
        .date-info div {
          margin-bottom: 1px;
        }
        
        .date-info .label {
          color: #dc2626;
          font-weight: 600;
        }
        
        .main-title {
          font-size: 22px;
          font-weight: bold;
          margin: 10px 0 6px 0;
        }
        
        .subtitle {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 6px;
        }
        
        .ticket-number {
          font-size: 18px;
          font-family: monospace;
          color: #dc2626;
          font-weight: bold;
        }
        
        .section {
          margin-bottom: 8px;
        }
        
        .section-title {
          font-size: 13px;
          font-weight: bold;
          margin-bottom: 5px;
          border-bottom: 1px solid #000;
          padding-bottom: 2px;
        }
        
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        
        .grid-3 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 3px;
          padding: 1px 0;
        }
        
        .info-label {
          font-weight: 600;
          color: #000;
        }
        
        .info-value {
          font-weight: 600;
          color: #dc2626;
        }
        
        .info-value.regular {
          color: #000;
          font-weight: normal;
        }
        
        .product-box {
          border: 1px solid #000;
          padding: 5px;
          border-radius: 2px;
        }
        
        .product-box h4 {
          font-size: 11px;
          font-weight: bold;
          margin-bottom: 4px;
        }
        
        .product-details {
          font-size: 10px;
        }
        
        .product-details .info-row {
          margin-bottom: 2px;
        }
        
        .weight-section {
          margin: 8px 0;
        }
        
        .weight-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 3px;
          padding: 2px 0;
        }
        
        .weight-label {
          font-weight: 600;
        }
        
        .weight-value {
          font-weight: 600;
          color: #dc2626;
        }
        
        .weight-value.large {
          font-size: 13px;
          font-weight: bold;
        }
        
        .weight-value.xlarge {
          font-size: 18px;
          font-weight: bold;
          color: #dc2626;
        }
        
        .sacs-section {
          margin-top: 8px;
        }
        
        .sacs-title {
          font-size: 11px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .sacs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 4px;
          font-size: 9px;
        }
        
        .sac-item {
          text-align: center;
          padding: 2px;
          border: 1px solid #ddd;
          border-radius: 2px;
        }
        
        .sac-label {
          color: #666;
          margin-bottom: 1px;
          font-size: 8px;
        }
        
        .sac-value {
          font-weight: bold;
          font-size: 11px;
          color: #dc2626;
        }
        
        .signatures {
          margin-top: 15px;
          padding-top: 10px;
          border-top: 1px solid #000;
        }
        
        .signatures-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 15px;
        }
        
        .signature-box {
          text-align: center;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #f9f9f9;
        }
        
        .signature-line {
          border-top: 1px solid #000;
          width: 100px;
          margin: 0 auto 8px auto;
          padding-top: 3px;
          height: 25px;
        }
        
        .signature-label {
          font-weight: bold;
          font-size: 10px;
          margin-bottom: 3px;
        }
        
        .signature-name {
          font-size: 9px;
          color: #666;
          margin-top: 3px;
          min-height: 15px;
        }
        
        .print-info {
          margin-top: 8px;
          text-align: center;
          font-size: 8px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 4px;
        }
        
        @media print {
          @page {
            size: A4 portrait;
            margin: 1.5cm;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          .ticket-container {
            border: none;
            padding: 0;
            max-width: 100%;
          }
          
          .no-print {
            display: none !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="ticket-container">
        <!-- En-tête du ticket -->
        <div class="header">
          <div class="logo-section">
            <div class="logo">
              <img src="/images/cca-logo.svg" alt="Logo CCA" class="logo-img" />
            </div>
            
            <div class="date-info">
              <div><span class="label">Date pesee:</span> ${ticketData.weighingDate || formatDate(ticketData.grossAt)}</div>
              <div><span class="label">Campagne:</span> ${ticketData.campaignYear || new Date().getFullYear()}</div>
              <div><span class="label">Code pesée:</span> ${ticketData.code}</div>
            </div>
            
            <div style="text-align: center;">
              <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" 
                   onerror="this.src='${fallbackQRCode}'; this.onerror=null;" />
            </div>
            
            <div style="text-align: center;">
              <img src="/images/logo-pontcita.png" alt="Logo Pont CITA" class="logo-pontcita" />
            </div>
          </div>
          
          <div class="main-title">TICKET DE PESEE CAJOU</div>
          <div class="subtitle">
            <span style="margin-right: 20px;">BON</span>
            <span>${ticketSubtitle}</span>
          </div>
          
          <div class="ticket-number">
            ${ticketData.ticketNumber || ''}
          </div>
        </div>

        <!-- Section Fournisseur -->
        <div class="section">
          <div class="section-title">Fournisseur</div>
          <div class="grid-2">
            <div>
              <div class="info-row">
                <span class="info-label">Identite:</span>
                <span class="info-value">${ticketData.supplierIdentity || ''}${ticketData.supplierCode ? `/${ticketData.supplierCode}` : ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Client:</span>
                <span class="info-value regular">${ticketData.clientCode || ''}${ticketData.clientCodeValue ? `/${ticketData.clientCodeValue}` : ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Exportateur:</span>
                <span class="info-value regular">${(ticketData.exporterName || ticketData.exporterCode) ? `${ticketData.exporterName || ''}${ticketData.exporterCode ? `/${ticketData.exporterCode}` : ''}` : '01'}</span>
              </div>
            </div>
            
            <div class="product-box">
              <h4>Produit : ${ticketData.product.toString().toUpperCase() || ''}</h4>
              <div class="product-details">
                <div class="info-row">
                  <span class="info-label">Nature :</span>
                  <span class="info-value regular">${ticketData.productNature || ''}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Origine:</span>
                  <span class="info-value regular">${ticketData.productOrigin || ''}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">N_Lot Brousse:</span>
                  <span class="info-value regular">${ticketData.bushLotNumber || ''}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section Identification -->
        <div class="section">
          <div class="section-title">Identification</div>
          <div class="grid-2">
            <div>
              <div class="info-row">
                <span class="info-label">Vehicule:</span>
                <span class="info-value">${ticketData.vehicle || ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Remorque:</span>
                <span class="info-value">${ticketData.trailerRegistration || ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Provenance:</span>
                <span class="info-value">${ticketData.provenance || ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">N° fiche transfert:</span>
                <span class="info-value">${ticketData.transferSheetNumber || ''}</span>
              </div>
            </div>
            
            <div>
              <div class="info-row">
                <span class="info-label">Transporteur:</span>
                <span class="info-value">${ticketData.transporterName || ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Destination:</span>
                <span class="info-value">${ticketData.destination || ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Chauffeur:</span>
                <span class="info-value">${ticketData.driver || ''}${ticketData.chauffeurCode ? `/${ticketData.chauffeurCode}` : ''}</span>
              </div>
              <div class="info-row">
                <span class="info-label">N° sticker:</span>
                <span class="info-value">${ticketData.stickerNumber || ''}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Section Poids et Sacherie -->
        <div class="section">
          <div class="section-title">Poids et Sacherie</div>
          <div class="grid-2">
            <div>
              <div class="weight-section">
                <div class="weight-row">
                  <span class="weight-label">Poids 1e Pesee:</span>
                  <span class="weight-value">${ticketData.firstWeighingWeight ? formatWeight(ticketData.firstWeighingWeight) : ''}</span>
                </div>
                <div style="font-size: 10px; color: #666; text-align: right; margin-bottom: 10px;">
                  ${ticketData.firstWeighingDateTime ? formatDateTime(ticketData.firstWeighingDateTime) : ''}
                </div>
                
                <div class="weight-row">
                  <span class="weight-label">Poids 2e Pesee:</span>
                  <span class="weight-value">${ticketData.secondWeighingWeight ? formatWeight(ticketData.secondWeighingWeight) : ''}</span>
                </div>
                <div style="font-size: 10px; color: #666; text-align: right; margin-bottom: 10px;">
                  ${ticketData.secondWeighingDateTime ? formatDateTime(ticketData.secondWeighingDateTime) : ''}
                </div>
                
                <div class="weight-row">
                  <span class="weight-label">Poids BRUT:</span>
                  <span class="weight-value large">${formatWeight(ticketData.grossWeight)}</span>
                </div>
                
                ${ticketData.analyse_qualite ? `
                <div class="weight-row">
                  <span class="weight-label">Niveau de qualité:</span>
                  <span class="weight-value">${ticketData.analyse_qualite.quality_level && ticketData.analyse_qualite.quality_level !== '' ? ticketData.analyse_qualite.quality_level : (ticketData.qualityLevel && ticketData.qualityLevel !== '' ? ticketData.qualityLevel : 'N/A')}</span>
                </div>
                
                <div class="weight-row">
                  <span class="weight-label">Grainnage:</span>
                  <span class="weight-value regular">${ticketData.analyse_qualite.grainnage && ticketData.analyse_qualite.grainnage !== '' ? ticketData.analyse_qualite.grainnage : (ticketData.grainnage && ticketData.grainnage !== '' ? ticketData.grainnage : 'N/A')}</span>
                </div>
                
                <div class="weight-row">
                  <span class="weight-label">Qualité:</span>
                  <span class="weight-value">${ticketData.analyse_qualite.quality !== undefined && ticketData.analyse_qualite.quality !== null && isValidValue(ticketData.analyse_qualite.quality) ? parseFloat(ticketData.analyse_qualite.quality.toString()).toFixed(2) : (ticketData.quality !== undefined && ticketData.quality !== null && isValidValue(ticketData.quality) ? parseFloat(ticketData.quality.toString()).toFixed(2) : 'N/A')}</span>
                </div>
                
                <div class="weight-row">
                  <span class="weight-label">Humidité:</span>
                  <span class="weight-value regular">${ticketData.analyse_qualite.humidite !== undefined && ticketData.analyse_qualite.humidite !== null && isValidValue(ticketData.analyse_qualite.humidite) ? parseFloat(ticketData.analyse_qualite.humidite.toString()).toFixed(2) + '%' : (ticketData.humidite !== undefined && ticketData.humidite !== null && isValidValue(ticketData.humidite) ? parseFloat(ticketData.humidite.toString()).toFixed(2) + '%' : 'N/A')}</span>
                </div>
                ` : ''}
                
                <div class="weight-row">
                  <span class="weight-label">Poids Sac:</span>
                  <span class="weight-value">${ticketData.poidsAcceptes ? formatWeight(ticketData.poidsAcceptes) : ''}</span>
                </div>
                
                <div class="weight-row" style="border-top: 2px solid #000; padding-top: 10px; margin-top: 10px;">
                  <span class="weight-label" style="font-size: 16px; font-weight: bold;">Poids Net :</span>
                  <span class="weight-value xlarge">${formatWeight(ticketData.netWeight)}</span>
                </div>
              </div>
            </div>
            
            <div class="sacs-section">
              <div class="sacs-title">Comptage des Sacs</div>
              <div class="sacs-grid">
                <div class="sac-item">
                  <div class="sac-label">Declares</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_declares || ''}</div>
                </div>
                <div class="sac-item">
                  <div class="sac-label">Sains</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_sains || ''}</div>
                </div>
                <div class="sac-item">
                  <div class="sac-label">Recond.</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_recond || ''}</div>
                </div>
                <div class="sac-item">
                  <div class="sac-label">Humides</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_humides || ''}</div>
                </div>
                <div class="sac-item">
                  <div class="sac-label">Dechires</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_dechires || ''}</div>
                </div>
                <div class="sac-item">
                  <div class="sac-label">Rejetes</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_rejetes || ''}</div>
                </div>
                <div class="sac-item">
                  <div class="sac-label">Acceptes</div>
                  <div class="sac-value">${ticketData.sacsCount?.nb_sacs_acceptes || ''}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Signatures -->
        <div class="signatures">
          <div class="signatures-grid">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">AGENT PESEUR</div>
              <div class="signature-name">${ticketData.weighingAgentSignature || ''}</div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-label">VISA CLIENT / FOURNISSEUR</div>
              <div class="signature-name">${ticketData.clientSupplierSignature || ''}</div>
            </div>
          </div>
        </div>

        <!-- Informations d'impression -->
        <div class="print-info">
          <div>Imprimé le ${new Date().toLocaleString('fr-FR')} - Système de Gestion des Pesées</div>
          <div>Ticket généré automatiquement - Ne pas reproduire sans autorisation</div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Génère un PDF téléchargeable du ticket à partir du HTML
 */
export const generatePDF = async (ticketData: WeighingTicketData): Promise<void> => {
  try {
    // Créer un élément temporaire pour contenir le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = generatePrintHTML(ticketData);
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = '210mm'; // A4 width
    tempDiv.style.backgroundColor = 'white';
    
    // Ajouter l'élément au DOM temporairement
    document.body.appendChild(tempDiv);
    
    // Attendre que les images se chargent
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Convertir le HTML en canvas
    const canvas = await html2canvas(tempDiv, {
      useCORS: true, // Permettre les images cross-origin
      allowTaint: true,
      background: '#ffffff',
      width: 794, // A4 width in pixels at 96 DPI
      height: tempDiv.scrollHeight,
      logging: false // Désactiver les logs
    });
    
    // Créer le PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    
    let position = 0;
    
    // Ajouter la première page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    // Ajouter des pages supplémentaires si nécessaire
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    // Télécharger le PDF
    const filename = `ticket_pesee_${ticketData.code}.pdf`;
    pdf.save(filename);
    
    // Nettoyer l'élément temporaire
    document.body.removeChild(tempDiv);
    
    console.log('✅ PDF généré avec succès');
    alert('✅ PDF généré et téléchargé avec succès !');
    
  } catch (error) {
    console.error('❌ Erreur lors de la génération PDF:', error);
    alert('❌ Erreur lors de la génération PDF. Veuillez réessayer.');
  }
};

/**
 * Génère le template d'email pour le ticket
 */
export const generateEmailTemplate = (ticketData: WeighingTicketData): string => {
  return `Bonjour,

Veuillez trouver ci-joint le ticket de pesée ${ticketData.code} pour la pesée effectuée le ${ticketData.weighingDate || new Date().toLocaleDateString('fr-FR')}.

Détails de la pesée :
- Référence : ${ticketData.code}
- Produit : ${ticketData.product}
- Véhicule : ${ticketData.vehicle}
- Poids brut : ${ticketData.grossWeight.toLocaleString()} kg
- Poids net : ${ticketData.netWeight.toLocaleString()} kg
- Fournisseur : ${ticketData.supplierIdentity || 'N/A'}${ticketData.supplierCode ? `/${ticketData.supplierCode}` : ''}
- Client : ${ticketData.clientCode || 'N/A'}${ticketData.clientCodeValue ? `/${ticketData.clientCodeValue}` : ''}
- Exportateur : ${(ticketData.exporterName || ticketData.exporterCode) ? `${ticketData.exporterName || ''}${ticketData.exporterCode ? `/${ticketData.exporterCode}` : ''}` : 'N/A'}
- Chauffeur : ${ticketData.driver || 'N/A'}${ticketData.chauffeurCode ? `/${ticketData.chauffeurCode}` : ''}

Le ticket PDF est joint à cet email.

Cordialement,
Service de Pesée - Conseil du Coton et de l'Anacarde`;
};

/**
 * Convertit un blob en base64
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
